import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layers, Flame, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import DivergenceBadge from "@/components/DivergenceBadge";

const CATEGORIES = ["all", "World", "Politics", "Tech", "Business", "Science", "Health"];

export default function TopicsPage() {
  const nav = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [category, setCategory] = useState("all");

  const load = async (cat = category) => {
    setLoading(true);
    try {
      const r = await api.get("/topics", { params: { limit: 40, category: cat } });
      setTopics(r.data);
    } catch {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(category); /* eslint-disable-next-line */ }, [category]);

  const recluster = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/news/refresh");
      toast.success(`Recomputed ${r.data.topics} hot topics from ${r.data.total_archive} stories`);
      await load();
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <section className="border-b-2 border-stone-900 pb-8 mb-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="font-mono-head text-xs uppercase tracking-[0.3em] text-stone-500 mb-3 flex items-center gap-2">
              <Layers className="w-3 h-3" /> Clustered Signals · {topics.length} hot topics
            </div>
            <h1 className="font-serif-head text-5xl sm:text-6xl text-stone-900 leading-[0.95] mb-4">
              The Hottest <span className="italic text-[#E60000]">Topics</span>.
            </h1>
            <p className="text-base text-stone-700 leading-relaxed max-w-2xl">
              Stories that multiple outlets are covering simultaneously — clustered by shared
              keywords, ranked by combined heat. Open any topic to see every angle.
            </p>
          </div>
          <button
            data-testid="topics-refresh-btn"
            onClick={recluster}
            disabled={refreshing}
            className="bg-stone-900 text-stone-50 font-mono-head text-sm uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#E60000] transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? "Re-clustering…" : "Recompute Topics"}
          </button>
        </div>
      </section>

      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`topic-cat-${c}`}
            onClick={() => setCategory(c)}
            className={`font-mono-head text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
              category === c
                ? "bg-stone-900 text-stone-50 border-stone-900"
                : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900"
            }`}
          >
            {c === "all" ? "All Topics" : c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Clustering…
        </div>
      ) : topics.length === 0 ? (
        <div className="py-16 text-center">
          <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500 mb-4">
            No multi-outlet topics yet. Click "Recompute Topics" after a refresh.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-300">
          {topics.map((t, i) => (
            <article
              key={t.id}
              data-testid={`topic-card-${t.id}`}
              onClick={() => nav(`/topics/${t.id}`)}
              className="bg-stone-50 p-6 cursor-pointer hover:bg-stone-100 transition-colors group"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="font-serif-head text-5xl text-[#E60000] leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-stone-900 bg-stone-200 px-2 py-1">
                    <Layers className="w-3 h-3" /> {t.count} outlets
                  </span>
                  <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-[#E60000]">
                    <Flame className="w-3 h-3" /> Heat {t.heat}
                  </span>
                </div>
              </div>
              <div className="font-mono-head text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">
                {t.category}
              </div>
              <h2 className="font-serif-head text-2xl text-stone-900 leading-tight mb-4 group-hover:text-[#E60000] transition-colors">
                {t.title}
              </h2>
              {t.divergence && (
                <div className="mb-3">
                  <DivergenceBadge divergence={t.divergence} />
                </div>
              )}
              <div className="flex flex-wrap gap-1 mb-3">
                {t.keywords.slice(0, 6).map((k) => (
                  <span key={k} className="font-mono-head text-[10px] uppercase tracking-wider text-stone-600 border border-stone-300 px-2 py-0.5">
                    {k}
                  </span>
                ))}
              </div>
              <div className="font-mono-head text-[10px] uppercase tracking-widest text-stone-500">
                {t.sources.slice(0, 4).join(" · ")}{t.sources.length > 4 ? " · …" : ""}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
